import type { NumericTextBoxChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { RadioGroup, Slider } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { NumericTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { useCallback, useEffect, useMemo, useState } from "react";

import { formatCarrierLiability } from "~/features/quote/utils/carrier-selection-utils.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import {
	SliderFilter,
	SliderFilterActions,
	SliderFilterField,
	SliderFilterInput,
	SliderFilterInputs,
} from "../slider-filter/slider-filter.tsx";

type LiabilityProduct = "new" | "used";

type LiabilityFilterProps = {
	carrierPrices: CarrierPrice[];
	onChange: (value: string[]) => void;
};

type SliderChangeEvent = {
	value: number;
};

type LiabilityOption = {
	label: string;
	newValue: number;
	usedValue: number;
};

const PRODUCT_OPTIONS: { label: string; value: LiabilityProduct }[] = [
	{ label: "New", value: "new" },
	{ label: "Used", value: "used" },
];

const SMALL_LIABILITY_STEP = 100;
const LARGE_LIABILITY_STEP = 1000;

const getLiabilityStep = (min: number, max: number): number => {
	return max - min < LARGE_LIABILITY_STEP
		? SMALL_LIABILITY_STEP
		: LARGE_LIABILITY_STEP;
};

const normalizeLiabilityValue = (value: number | null | undefined): number => {
	return typeof value === "number" && Number.isFinite(value) ? value : 0;
};

const clamp = (value: number, minValue: number, maxValue: number): number => {
	return Math.min(Math.max(value, minValue), maxValue);
};

const snapToStep = (value: number, step: number): number => {
	if (!Number.isFinite(step) || step <= 0) {
		return value;
	}

	return Math.round(value / step) * step;
};

const getLiabilityValue = (
	option: LiabilityOption,
	product: LiabilityProduct,
): number => {
	return product === "new" ? option.newValue : option.usedValue;
};

const getUniqueLabelsInRange = (
	options: LiabilityOption[],
	product: LiabilityProduct,
	minValue: number,
	maxValue: number,
): string[] => {
	const labels = new Set<string>();

	for (const option of options) {
		const liabilityValue = getLiabilityValue(option, product);

		if (liabilityValue >= minValue && liabilityValue <= maxValue) {
			labels.add(option.label);
		}
	}

	return [...labels];
};

export const LiabilityFilter = ({
	carrierPrices,
	onChange,
}: LiabilityFilterProps) => {
	const [selectedProduct, setSelectedProduct] =
		useState<LiabilityProduct>("new");
	const [selectedMin, setSelectedMin] = useState<number | null>(null);

	const liabilityOptions = useMemo<LiabilityOption[]>(() => {
		return carrierPrices.map((carrierPrice) => ({
			label: formatCarrierLiability(carrierPrice),
			newValue: normalizeLiabilityValue(carrierPrice.maxLiabilityNew),
			usedValue: normalizeLiabilityValue(carrierPrice.maxLiabilityUsed),
		}));
	}, [carrierPrices]);

	const productValues = useMemo(() => {
		return liabilityOptions.map((option) =>
			getLiabilityValue(option, selectedProduct),
		);
	}, [liabilityOptions, selectedProduct]);

	const hasLiabilityValues = productValues.length > 0;
	const normalizedMin = hasLiabilityValues ? Math.min(...productValues) : 0;
	const normalizedMax = hasLiabilityValues ? Math.max(...productValues) : 0;
	const hasValidRange = hasLiabilityValues && normalizedMin < normalizedMax;
	const liabilityStep = getLiabilityStep(normalizedMin, normalizedMax);
	const currentMin = selectedMin ?? normalizedMin;
	const selectedText = selectedMin
		? `(Min ${formatCurrency(selectedMin)})`
		: "";

	useEffect(() => {
		setSelectedMin(null);
	}, [carrierPrices, selectedProduct]);

	const updateSelectedMin = useCallback(
		(value: number) => {
			const nextMin = clamp(value, normalizedMin, normalizedMax);

			setSelectedMin(nextMin);
			onChange(
				getUniqueLabelsInRange(
					liabilityOptions,
					selectedProduct,
					nextMin,
					normalizedMax,
				),
			);
		},
		[liabilityOptions, normalizedMax, normalizedMin, onChange, selectedProduct],
	);

	const handleSliderChange = (event: SliderChangeEvent) => {
		updateSelectedMin(snapToStep(event.value, liabilityStep));
	};

	const handleMinChange = (event: NumericTextBoxChangeEvent) => {
		if (event.value === null) {
			return;
		}

		updateSelectedMin(event.value);
	};

	const handleProductChange = (event: { value: LiabilityProduct }) => {
		setSelectedProduct(event.value);
		onChange([]);
	};

	return (
		<SliderFilter
			hasValidRange={hasValidRange}
			label={"Liability"}
			popupTestId={"liability-filter-popup"}
			selectedText={selectedText}
			triggerTestId={"liability-filter"}
		>
			{({ closePopup }) => {
				const handleReset = () => {
					setSelectedMin(null);
					onChange([]);
					closePopup();
				};

				return (
					<>
						<SliderFilterField label={"Product"} required={true}>
							<RadioGroup
								data={PRODUCT_OPTIONS}
								layout={"horizontal"}
								onChange={handleProductChange}
								value={selectedProduct}
							/>
						</SliderFilterField>
						<Slider
							largeStep={liabilityStep}
							max={normalizedMax}
							min={normalizedMin}
							onChange={handleSliderChange}
							step={liabilityStep}
							style={{ width: "100%" }}
							value={currentMin}
						/>
						<SliderFilterInputs>
							<SliderFilterInput>
								<Label editorId={"liabilityFilterMin"}>Min</Label>
								<NumericTextBox
									format={"c2"}
									id={"liabilityFilterMin"}
									max={normalizedMax}
									min={normalizedMin}
									onChange={handleMinChange}
									spinners={false}
									value={currentMin}
									width={90}
								/>
							</SliderFilterInput>

							<SliderFilterInput disabled={true}>
								{formatCurrency(normalizedMax)}
							</SliderFilterInput>
						</SliderFilterInputs>
						<SliderFilterActions>
							<Button onClick={handleReset}>Reset</Button>
						</SliderFilterActions>
					</>
				);
			}}
		</SliderFilter>
	);
};
